import type { ReactNode } from 'react';

import StatusDot, { type StatusDotTone } from '@/components/atoms/StatusDot';
import {
    type AuditFilterChange,
    type AuditLogEntry,
    type AuditOutcome,
    addAuditFilters,
    describeAuditOutcome,
    useAuditText,
} from '@/domain/auditLog';

import CellFilterButton from './CellFilterButton';
import CellLines from './CellLines';

import styles from './OutcomeCell.module.scss';

const OUTCOME_TONE: Record<AuditOutcome, StatusDotTone> = {
    success: 'success',
    denied: 'warning',
    failed: 'danger',
};

interface OutcomeMarkProps {
    outcome: AuditOutcome;
    children: ReactNode;
}

/**
 * An outcome as the table and the details header show it. Every outcome has the same
 * shape, so only the dot's colour sets a denied or failed row apart from the success rows
 * around it.
 */
export function OutcomeMark({ outcome, children }: OutcomeMarkProps) {
    return (
        <StatusDot tone={OUTCOME_TONE[outcome]} size="sm" className={styles.mark}>
            {children}
        </StatusDot>
    );
}

interface OutcomeCellProps {
    entry: AuditLogEntry;
    onFilter: (change: AuditFilterChange) => void;
}

export default function OutcomeCell({ entry, onFilter }: OutcomeCellProps) {
    const render = useAuditText();
    const outcome = describeAuditOutcome(entry);
    const word = render(outcome.word);
    const { code, statusFilter } = outcome;
    const wordButton = (
        <CellFilterButton value={word} onFilter={() => onFilter(addAuditFilters(outcome.filter))}>
            {word}
        </CellFilterButton>
    );

    return (
        <CellLines
            first={
                <OutcomeMark outcome={outcome.outcome}>
                    {wordButton}
                    {code === null || statusFilter === null ? null : (
                        <CellFilterButton
                            value={code}
                            onFilter={() => onFilter(addAuditFilters(statusFilter))}>
                            {code}
                        </CellFilterButton>
                    )}
                </OutcomeMark>
            }
            firstTitle={render(outcome.label)}
        />
    );
}
